const DB_NAME = 'SegavanOffline';
const DB_VERSION = 3;

const STORES = {
  API_CACHE:  'apiCache',   // { url (key), data, ts }
  SYNC_QUEUE: 'syncQueue',  // { id (auto), method, url, data, status, ts }
  META:       'meta',       // { key, value } — shared with service worker
};

class OfflineDB {
  constructor() {
    this.db = null;
    this._ready = null;
  }

  init() {
    if (this._ready) return this._ready;
    this._ready = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => { this.db = req.result; resolve(this.db); };
      req.onupgradeneeded = (e) => {
        const db = e.target.result;
        if (!db.objectStoreNames.contains('apiCache'))
          db.createObjectStore('apiCache', { keyPath: 'url' });
        if (!db.objectStoreNames.contains('syncQueue')) {
          const sq = db.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true });
          sq.createIndex('status', 'status', { unique: false });
        }
        if (!db.objectStoreNames.contains('meta'))
          db.createObjectStore('meta', { keyPath: 'key' });
      };
    });
    return this._ready;
  }

  async _db() {
    if (!this.db) await this.init();
    return this.db;
  }

  // ── API cache (GET responses keyed by URL) ─────────────────────────────────

  async saveResponse(url, data) {
    const db = await this._db();
    const tx = db.transaction('apiCache', 'readwrite');
    tx.objectStore('apiCache').put({ url, data, ts: Date.now() });
    return new Promise((res, rej) => { tx.oncomplete = res; tx.onerror = () => rej(tx.error); });
  }

  async getResponse(url) {
    const db = await this._db();
    const tx = db.transaction('apiCache', 'readonly');
    return new Promise((res, rej) => {
      const r = tx.objectStore('apiCache').get(url);
      r.onsuccess = () => res(r.result ? r.result.data : null);
      r.onerror = () => rej(r.error);
    });
  }

  // ── Write queue (POST/PUT/PATCH/DELETE offline) ───────────────────────────

  async queueSync(method, url, data) {
    const db = await this._db();
    const item = { method, url, data, status: 'pending', ts: Date.now(), retries: 0 };
    const tx = db.transaction('syncQueue', 'readwrite');
    return new Promise((res, rej) => {
      const r = tx.objectStore('syncQueue').add(item);
      r.onsuccess = () => res(r.result);
      r.onerror = () => rej(r.error);
    });
  }

  async getPendingSync() {
    const db = await this._db();
    const tx = db.transaction('syncQueue', 'readonly');
    return new Promise((res, rej) => {
      const r = tx.objectStore('syncQueue').getAll();
      r.onsuccess = () => res((r.result || []).filter(i => i.status === 'pending'));
      r.onerror = () => rej(r.error);
    });
  }

  async markSyncDone(id) {
    const db = await this._db();
    const tx = db.transaction('syncQueue', 'readwrite');
    const store = tx.objectStore('syncQueue');
    const item = await new Promise((res, rej) => {
      const r = store.get(id);
      r.onsuccess = () => res(r.result);
      r.onerror = () => rej(r.error);
    });
    if (item) { item.status = 'done'; store.put(item); }
    return new Promise((res, rej) => { tx.oncomplete = res; tx.onerror = () => rej(tx.error); });
  }

  // ── Meta key-value (shared with service worker) ──────────────────────────
  async setMeta(key, value) {
    const db = await this._db();
    const tx = db.transaction('meta', 'readwrite');
    tx.objectStore('meta').put({ key, value });
    return new Promise((res, rej) => { tx.oncomplete = res; tx.onerror = () => rej(tx.error); });
  }

  async getMeta(key) {
    const db = await this._db();
    const tx = db.transaction('meta', 'readonly');
    return new Promise((res, rej) => {
      const r = tx.objectStore('meta').get(key);
      r.onsuccess = () => res(r.result?.value ?? null);
      r.onerror   = () => rej(r.error);
    });
  }

  async getSize() {
    if (!navigator.storage?.estimate) return { usage: 0, quota: 0 };
    const e = await navigator.storage.estimate();
    return { usage: Math.round((e.usage || 0) / 1048576), quota: Math.round((e.quota || 0) / 1048576) };
  }
}

const offlineDB = new OfflineDB();
export default offlineDB;
export { STORES };
